/**
 * UI actions: core ops run with a LocalOrigin (so Cmd/Ctrl+Z works) and, for structural
 * or destructive actions, an Undo toast (SPEC §6.2).
 */
import * as Y from 'yjs'
import * as core from '@terrazzo/core'
import {
  displayTitle,
  formatDayShort,
  getYItem,
  itemsMap,
  relativeDayLabel,
  type Attachment,
  type FieldDef,
  type OpContext,
  type Position,
  type Recurrence,
  type ReminderKind,
  type SavedView,
  type Tint,
} from '@terrazzo/core'
import { doc, LocalOrigin } from '../sync/doc'
import { store } from './store'
import { myId } from './me'
import { currentToday } from './hooks'
import { withUndo } from './undo'
import { showToast } from './toasts'
import { haptic } from '../lib/haptics'
import { forget } from '../lib/uploadQueue'
import { chartsOf, homeKey, ideasSeenOf, type ChartHome } from '../lib/charts'

const LOCAL = new LocalOrigin('edit')
/** Writes that sync but aren't anyone's edit to undo (a chart idea seen). */
const QUIET = { quiet: true }
/** Settings changed on their own (a theme, a layout): they sync, but Cmd+Z doesn't step through them. */
const SETTING = { setting: true }

export function ctx(origin: unknown = LOCAL): OpContext {
  return {
    doc,
    actor: myId(),
    today: currentToday(),
    tz: store.household().timezone,
    origin,
    tree: store.tree,
  }
}

const t = (id: string) => displayTitle(store.get(id)?.title ?? '', (x) => store.get(x)?.title) || 'Untitled'

function guard<T>(fn: () => T): T | undefined {
  try {
    return fn()
  } catch (e) {
    if (e instanceof core.OpError) {
      showToast(e.message)
      return undefined
    }
    throw e
  }
}

/** A detail that holds numbers (made, or a text one with no values yet turned into one). */
function ensureNumberField(key: string, type: 'money' | 'number', label?: string) {
  const def = store.fields().get(key)
  if (def && (def.type === 'money' || def.type === 'number')) return
  if (def && [...store.raw.values()].some((it) => core.hasValue(it.props[key]) && !core.isEq(it.props[key]))) return
  actions.addField({ key, type, label: def?.label ?? label ?? key })
}

export const actions = {
  addItem(init: core.AddInit): string {
    return core.addItem(ctx(), init)
  },

  addRows(pos: Position, titles: string[], extra: Omit<core.ItemInit, 'title'> = {}): string[] {
    return core.addItems(ctx(), pos, titles, extra)
  },

  /** Write an item's notes from markdown (a new note from Add: the same undo step as adding it). */
  async setBodyMarkdown(id: string, md: string) {
    const { setFragmentFromMarkdown } = await import('@terrazzo/core/richtext')
    doc.transact(() => {
      const y = getYItem(doc, id)
      if (!y) return
      setFragmentFromMarkdown(core.ensureBody(y), md)
      core.touch(y, myId())
    }, LOCAL)
  },

  toggleDone(id: string) {
    const it = store.get(id)
    if (!it) return
    haptic()
    const want = it.done !== true
    withUndo(
      (r: core.DoneResult) => (r.kind === 'resting' ? `Done · back ${formatDayShort(r.next)}` : r.kind === 'recurred' ? `Next: ${formatDayShort(r.next)}` : want ? 'Done' : 'Reopened'),
      (origin) => core.setDone(ctx(origin), id, want),
    )
  },

  /** Uncheck a recurring completion shown in a Day card's Done list. */
  undoCompletion(id: string, at: number) {
    withUndo('Completion removed', (origin) => core.undoRecurringCompletion(ctx(origin), id, at))
  },

  skipRecurrence(id: string) {
    withUndo(
      (next: string | null) => (next ? `Skipped · next ${formatDayShort(next)}` : 'Skipped'),
      (origin) => core.skipRecurrence(ctx(origin), id),
    )
  },

  setCheckbox(id: string, on: boolean) {
    core.setCheckbox(ctx(), id, on)
  },

  /** Swiping an item's title left on its page: no checkbox (Undo brings it back, ticked or not). */
  removeCheckbox(id: string) {
    haptic()
    withUndo('Removed the checkbox', (origin) => core.setCheckbox(ctx(origin), id, false))
  },

  /** Outdenting a task past the top level: it becomes a heading (no checkbox). */
  makeHeading(id: string) {
    haptic()
    withUndo('Turned into a heading', (origin) => core.setCheckbox(ctx(origin), id, false))
  },

  /** Toast by default (SPEC §6.2); Tab/Shift-Tab while typing passes toast:false (Cmd+Z still works). */
  indent(id: string, newParentId: string, opts: { toast?: boolean } = {}) {
    guard(() => withUndo(`Indented under ${t(newParentId)}`, (origin) => core.indentItem(ctx(origin), id, newParentId), opts))
    haptic()
  },

  outdent(id: string, opts: { toast?: boolean } = {}) {
    guard(() => withUndo('Outdented', (origin) => core.outdentItem(ctx(origin), id), opts))
    haptic()
  },

  move(id: string, pos: Position, message?: string) {
    return guard(() => withUndo(message ?? (pos.parentId ? `Moved to ${t(pos.parentId)}` : 'Moved to top level'), (origin) => core.moveItem(ctx(origin), id, pos)))
  },

  /** Reorder within the same parent (drag within a card; the phone edit bar passes toast:false). */
  reorder(id: string, pos: Position, opts: { toast?: boolean } = {}) {
    return guard(() => withUndo('Moved', (origin) => core.moveItem(ctx(origin), id, pos), opts))
  },

  /**
   * Rearrange cards on a board: put card `id` just before/after `targetId`. On Home pinned
   * cards come first, so landing among them pins it and landing among the rest unpins it;
   * the Inbox stays first and the Agent log last. `asNewCard`: a row dragged out into the open,
   * its own card now (with what's inside it), which always says so (with Undo).
   */
  placeCard(id: string, targetId: string, side: 'before' | 'after', opts: { asNewCard?: boolean } = {}) {
    const cur = store.get(id)
    if (!cur || cur.system || id === targetId) return
    const parentId = store.tree.parentOf.get(targetId) ?? null
    let target = targetId
    let where = side
    if (parentId === null) {
      if (store.get(target)?.system === 'inbox') {
        const order = core.boardCards(store.idx).filter((c) => c !== id)
        const next = order[order.indexOf(target) + 1]
        if (!next || store.get(next)?.system) return
        target = next
        where = 'before'
      }
      if (store.get(target)?.system === 'agent-log') where = 'before'
    }
    const pin = parentId === null ? !!store.get(target)?.pinned && !store.get(target)?.system : undefined
    const pinChanged = pin !== undefined && !!cur.pinned !== pin
    guard(() =>
      withUndo(
        opts.asNewCard ? `“${t(id)}” is its own card now` : pin ? `Pinned “${t(id)}”` : `Unpinned “${t(id)}”`,
        (origin) =>
          doc.transact(() => {
            core.moveItem(ctx(origin), id, { parentId, ...(where === 'before' ? { beforeId: target } : { afterId: target }) })
            if (pinChanged) core.patchItem(ctx(origin), id, { pinned: pin })
          }, origin),
        // Just moving it is plain to see; a pin change, or a row becoming a card, gets a toast (and Undo).
        { toast: pinChanged || !!opts.asNewCard },
      ),
    )
  },

  trash(id: string, opts: { toast?: boolean } = {}) {
    return guard(() => withUndo(`“${t(id)}” moved to Trash`, (origin) => core.trashItem(ctx(origin), id), opts))
  },

  /**
   * Backspace on an empty row: a bare row goes quietly (Cmd+Z still works); one carrying
   * notes, a date, files etc. gets the regular Undo toast.
   */
  trashEmpty(id: string) {
    const it = store.get(id)
    if (it && isPlainRow(it)) guard(() => core.trashItem(ctx(), id))
    else actions.trash(id)
  },

  /** Backspace at the start of a row: append its title to `prevId` and drop it (one undo step). */
  mergeInto(prevId: string, id: string) {
    const prev = store.get(prevId)
    const it = store.get(id)
    if (!prev || !it) return
    guard(() =>
      withUndo(
        'Merged with the row above',
        (origin) => {
          doc.transact(() => {
            const y = getYItem(doc, prevId)
            if (!y) return
            core.applyTextDiff(core.ensureTitle(y), prev.title + it.title)
            core.touch(y, myId())
            core.trashItem(ctx(origin), id)
          }, origin)
        },
        { toast: false },
      ),
    )
  },

  restore(id: string) {
    guard(() => withUndo('Restored', (origin) => core.restoreItem(ctx(origin), id)))
  },

  archive(ids: string[]) {
    if (!ids.length) return
    withUndo(ids.length === 1 ? 'Archived' : `Archived ${ids.length} items`, (origin) => core.archiveItems(ctx(origin), ids))
  },

  unarchive(id: string) {
    withUndo('Unarchived', (origin) => core.unarchiveItem(ctx(origin), id))
  },

  purge(id: string) {
    const keys = [id, ...core.descendantsOf(store.tree, id)].flatMap((x) => attachmentKeys(store.get(x)?.attachments ?? []))
    core.purgeItem(ctx(new LocalOrigin('purge')), id)
    if (keys.length) void forget(keys)
  },

  /** `alignRepeat`: the date picker — a repeat follows the new date (see core setSchedule). */
  schedule(id: string, patch: core.DatePatch, opts: { toast?: boolean; alignRepeat?: boolean } = {}) {
    const today = currentToday()
    withUndo(
      patch.date === null ? 'Unscheduled' : patch.date ? `Moved to ${relativeDayLabel(patch.date, today)}` : 'Updated',
      (origin) => core.setSchedule(ctx(origin), id, patch, { alignRepeat: opts.alignRepeat }),
      opts,
    )
  },

  placeOnDay(
    id: string,
    date: string,
    neighbours: {
      beforeDayOrder?: string | null
      afterDayOrder?: string | null
    },
  ) {
    const today = currentToday()
    const same = store.get(id)?.date === date
    withUndo(`Moved to ${relativeDayLabel(date, today)}`, (origin) => core.placeOnDay(ctx(origin), id, date, neighbours), { toast: !same })
  },

  moveDates(moves: Array<{ id: string; date: string }>, message: string) {
    if (!moves.length) return
    withUndo(message, (origin) => core.moveDates(ctx(origin), moves))
  },

  setRecurrence(id: string, rule: Recurrence | null) {
    core.setRecurrence(ctx(), id, rule)
  },

  setReminders(id: string, kinds: ReminderKind[]) {
    core.setReminders(ctx(), id, kinds)
  },

  patch(id: string, patch: core.ItemPatch) {
    core.patchItem(ctx(), id, patch)
  },

  setTint(id: string, tint: Tint) {
    core.patchItem(ctx(), id, { tint })
  },

  /** The subtitle of the rows inside this item (see core `subtitleFor`; null = the default, money). */
  /**
   * Arrange an item's page: kept on the item itself (`where` = its id) or on a card for
   * everything inside it (then the item's own goes). null = back to automatic there.
   */
  arrangePage(id: string, where: string, order: core.PageSection[] | null) {
    withUndo(
      order ? 'Arranged the page' : 'Page back to automatic',
      (origin) => {
        if (where === id) core.patchItem(ctx(origin), id, { pageOrder: order })
        else {
          core.patchItem(ctx(origin), where, { childPageOrder: order })
          if (store.get(id)?.pageOrder) core.patchItem(ctx(origin), id, { pageOrder: null })
        }
      },
      { toast: false },
    )
  },

  setRowFields(id: string, keys: string[] | null) {
    withUndo('Changed the subtitle', (origin) => core.patchItem(ctx(origin), id, { rowFields: keys }), { toast: false })
  },

  /** A card's subtitle just in one tab (`viewId`); null goes back to the card's own. */
  setTabSubtitle(viewId: string, cardId: string, keys: string[] | null) {
    const view = store.get(viewId)?.props.view as core.SavedView | undefined
    if (!view) return
    const subtitles = { ...(view.subtitles ?? {}) }
    if (keys) subtitles[cardId] = keys
    else delete subtitles[cardId]
    const next: core.SavedView = { ...view, subtitles }
    if (!Object.keys(subtitles).length) delete next.subtitles
    withUndo('Changed the subtitle in this tab', (origin) => core.patchItem(ctx(origin), viewId, { props: { view: next } }), { toast: false })
  },

  /** A group card's color in a tab (a group isn't an item, so the tab keeps it); 'none' removes it. */
  setTabTint(viewId: string, key: string, tint: Tint) {
    const view = store.get(viewId)?.props.view as core.SavedView | undefined
    if (!view) return
    const tints = { ...(view.tints ?? {}) }
    if (tint && tint !== 'none') tints[key] = tint
    else delete tints[key]
    const next: core.SavedView = { ...view, tints }
    if (!Object.keys(tints).length) delete next.tints
    core.patchItem(ctx(), viewId, { props: { view: next } })
  },

  /** An emoji icon for the item (a page's, mostly); null removes it. */
  setIcon(id: string, icon: string | null) {
    withUndo(icon ? 'Icon set' : 'Icon removed', (origin) => core.patchItem(ctx(origin), id, { icon }), { toast: false })
  },

  togglePin(id: string) {
    const it = store.get(id)
    core.patchItem(ctx(), id, { pinned: !it?.pinned })
  },

  setProp(id: string, key: string, value: unknown) {
    core.patchItem(ctx(), id, { props: { [key]: value } })
  },

  share(id: string): string {
    const token = core.randomToken(16)
    core.patchItem(ctx(), id, { shareToken: token })
    return token
  },

  revokeShare(id: string) {
    core.patchItem(ctx(), id, { shareToken: null })
  },

  putAttachment(id: string, att: Attachment) {
    core.putAttachment(ctx(), id, att)
  },

  updateAttachment(id: string, attId: string, patch: Partial<Attachment>) {
    core.updateAttachment(ctx(), id, attId, patch)
  },

  /** Undoable, so the local copy stays until the next cache prune (see pruneBlobs). */
  /** The photo or file goes, and with it any place its note shows it (one Undo brings both back). */
  removeAttachment(id: string, attId: string, opts: { toast?: boolean } = {}) {
    withUndo(
      'Attachment removed',
      (origin) => {
        core.removeAttachment(ctx(origin), id, attId)
        const y = core.getYItem(doc, id)
        if (y) doc.transact(() => core.removeBodyImages(core.yBody(y), attId), origin)
      },
      opts,
    )
  },

  addField(def: FieldDef) {
    doc.transact(() => core.fieldsMap(doc).set(def.key, def), LOCAL)
  },

  /** Where a detail name shows on item pages: its quick line or its grid (null = its default). Shared. */
  setDetailPlace(name: string, place: core.DetailPlace | null) {
    const cur = { ...store.household().detailPlacement }
    if (place) cur[name] = place
    else delete cur[name]
    actions.setHousehold('detailPlacement', cur)
  },

  /** One tag swapped for another on an item (changing a detail's value). */
  replaceTag(id: string, from: string, to: string) {
    const n = core.normalizeTag(to)
    if (!n || n === from) return
    core.patchItem(ctx(), id, { removeTags: [from], addTags: [n] })
  },

  /**
   * Details tidied everywhere at once (Settings → Details): every tag at or under `from` moves
   * under `to` (renaming, or merging into a name already in use), or goes (`to` = null).
   */
  moveTagsEverywhere(from: string, to: string | null) {
    const next = to === null ? null : core.normalizeTag(to)
    if (next === '' || next === from) return 0
    const hits = [...store.idx.items.values()].filter((it) => !store.tree.inTrash.has(it.id) && it.tags.some((t) => core.isUnderTag(t, from)))
    if (!hits.length) return 0
    withUndo(
      next === null ? `Removed #${from} from ${hits.length} ${hits.length === 1 ? 'item' : 'items'}` : `#${from} → #${next} on ${hits.length} ${hits.length === 1 ? 'item' : 'items'}`,
      (origin) => {
        for (const it of hits) {
          const tags = it.tags.flatMap((t) => (core.isUnderTag(t, from) ? (next === null ? [] : [next + t.slice(from.length)]) : [t]))
          core.patchItem(ctx(origin), it.id, { tags: [...new Set(tags)] })
        }
        // Equations reading the group (`plus/2` → {t:plus}) follow it to its new name.
        if (next !== null && !from.includes('/')) core.retagEquations(ctx(origin), from, next)
      },
    )
    return hits.length
  },

  /** A field's value cleared on every item (Settings → Details → Remove), its equations too. */
  clearFieldEverywhere(key: string) {
    const hits = [...store.raw.values()].filter((it) => core.hasValue(it.props[key]))
    const rules = [...store.raw.values()].filter((it) => it.calcInside?.[key])
    if (!hits.length && !rules.length) return 0
    const label = store.fields().get(key)?.label ?? key
    withUndo(`Removed ${label} from ${hits.length} ${hits.length === 1 ? 'item' : 'items'}`, (origin) => {
      for (const it of hits) core.patchItem(ctx(origin), it.id, { props: { [key]: null } })
      for (const it of rules) core.patchItem(ctx(origin), it.id, { calcInside: { [key]: null } })
    })
    return hits.length
  },

  /**
   * A detail worked out (SPEC §7.12), `stored` as core keeps it: on the item itself, or for
   * everything in `card` (then the item's own goes). `type`: what its answers are (money or a
   * number), for a detail new to the household or one with no typed values yet.
   */
  setEquation(id: string, key: string, stored: string, opts: { card?: string; type?: 'money' | 'number'; label?: string } = {}) {
    ensureNumberField(key, opts.type ?? 'number', opts.label)
    withUndo(
      'Equation changed',
      (origin) => {
        if (opts.card) {
          core.patchItem(ctx(origin), opts.card, { calcInside: { [key]: stored } })
          if (core.isEq(store.raw.get(id)?.props[key])) core.patchItem(ctx(origin), id, { props: { [key]: null } })
        } else {
          core.patchItem(ctx(origin), id, { props: { [key]: { eq: stored } } })
          // Just this item now: its card's no longer sets it for everything.
          const card = store.get(id)?.calc?.[key]?.card
          if (card) core.patchItem(ctx(origin), card, { calcInside: { [key]: null } })
        }
      },
      { toast: false },
    )
  },

  /** A worked-out detail taken off: the item's own, or its card's for everything inside. */
  removeEquation(id: string, key: string) {
    const c = store.get(id)?.calc?.[key]
    const label = store.fields().get(key)?.label ?? key
    withUndo(`Removed ${label}`, (origin) => {
      if (c?.from === 'card' && c.card) core.patchItem(ctx(origin), c.card, { calcInside: { [key]: null } })
      if (core.isEq(store.raw.get(id)?.props[key])) core.patchItem(ctx(origin), id, { props: { [key]: null } })
      if (store.get(id)?.total === key) core.patchItem(ctx(origin), id, { total: null })
    })
  },

  /**
   * A card's total (SPEC §7.12): a detail on it adding up `of` over what's inside (everything,
   * what's open, or what's done), shown on its bar. null takes it off (back to the automatic one).
   */
  setCardTotal(id: string, of: string | null, adds: 'all' | 'open' | 'done' = 'all') {
    if (of === null && !store.get(id)?.total) return
    withUndo(of === null ? 'Total off' : 'Total changed', (origin) => core.setCardTotal(ctx(origin), id, of === null ? null : store.fields().has(of) ? of : `#${of}`, adds), { toast: of === null })
  },

  /** A field's name, everywhere. */
  renameField(key: string, label: string) {
    const def = store.fields().get(key)
    const l = label.trim()
    if (!def || !l || l === def.label) return
    doc.transact(() => core.fieldsMap(doc).set(key, { ...def, label: l }), LOCAL)
  },

  setHousehold<K extends keyof core.HouseholdSettings>(key: K, value: core.HouseholdSettings[K]) {
    doc.transact(() => core.setHouseholdSetting(doc, key, value), SETTING)
  },

  setMine(key: string, value: unknown) {
    doc.transact(() => core.setUserSetting(doc, myId(), key, value), SETTING)
  },

  /** Pages sidebar: make `id` a page (optionally at `pos`), or send it back to the board. */
  setPage(id: string, on: boolean, pos?: Position) {
    const was = store.get(id)?.page === true
    const msg = !on ? 'Moved back to the board' : !was ? `“${t(id)}” is now a page` : pos?.parentId ? `Moved under ${t(pos.parentId)}` : 'Moved'
    return guard(() => withUndo(msg, (origin) => core.setPage(ctx(origin), id, on, pos)))
  },

  /** Put card `id` inside card `target`, at the end of its list (held over it on Home). */
  moveInto(id: string, target: string) {
    const cur = store.get(id)
    if (!cur || cur.system || id === target) return
    guard(() =>
      withUndo(`Moved into “${t(target)}”`, (origin) =>
        doc.transact(() => {
          core.moveItem(ctx(origin), id, { parentId: target, at: 'end' })
          if (cur.pinned) core.patchItem(ctx(origin), id, { pinned: false })
        }, origin),
      ),
    )
  },

  /**
   * Make it a plain note in the Inbox, so it's on Notes: off the Pages tree (its subpages come
   * along as its own items), or out of wherever it was — without a checkbox.
   */
  toNote(id: string) {
    const it = store.get(id)
    if (!it || it.system) return
    if (!it.page && it.done === null && store.tree.parentOf.get(id) === core.INBOX_ID) return
    guard(() =>
      withUndo(`“${t(id)}” is now a note`, (origin) =>
        doc.transact(() => {
          const c = ctx(origin)
          for (const d of core.descendantsOf(store.tree, id)) if (store.get(d)?.page) core.patchItem(c, d, { page: false })
          if (it.page || it.pinned) core.patchItem(c, id, { page: false, pinned: false })
          core.moveItem(c, id, { parentId: core.INBOX_ID, at: 'start' })
          if (it.done !== null) core.setCheckbox(c, id, false)
        }, origin),
      ),
    )
  },

  /** Per card: how its checked-off items show (tucked into Done, at the bottom, or in place). */
  setDoneStyle(id: string, style: core.DoneStyle) {
    const labels = { collapse: 'Completed items tucked into Done', bottom: 'Completed items move to the bottom', inline: 'Completed items stay in place' }
    withUndo(labels[style], (origin) => core.patchItem(ctx(origin), id, { doneStyle: style }))
  },

  /** Turn into a page: a subpage when it already sits in a page, else a top-level page. */
  makePage(id: string) {
    const parent = store.tree.parentOf.get(id) ?? null
    return actions.setPage(id, true, { parentId: parent && store.get(parent)?.page ? parent : null, at: 'end' })
  },

  /**
   * Dragging a card to the tab bar: a shared tab whose filter is just "inside this card" (open
   * and done, as the card shows them) — the same form as any other tab, only pre-filled.
   */
  pinAsTab(id: string): string | undefined {
    // A card can have several tabs (each grouped or filtered its own way).
    const again = [...store.snaps.values()].some((v) => !store.tree.inTrash.has(v.id) && !!v.props.view && core.pinnedItemOf(v.props.view as SavedView) === id)
    return withUndo(`Pinned “${t(id)}” as ${again ? 'another' : 'a'} tab`, (origin) =>
      core.addItem(ctx(origin), {
        parentId: null,
        title: '',
        done: null,
        props: { view: { filter: { ancestors: [id], tagMode: 'all' }, layout: 'list', groupBy: 'card' } satisfies SavedView },
        at: 'end',
      }),
    )
  },

  /** Dropping a card on a tab: add it to the tab's "Inside" cards. */
  addToTab(viewId: string, id: string) {
    const raw = store.get(viewId)?.props.view as SavedView | undefined
    if (!raw) return
    const view = core.normalizeView(raw)
    const inside = view.filter.ancestors ?? []
    const tabName = viewItemTitleOf(viewId)
    if (!inside.length) return void showToast(`“${tabName}” already shows items from everywhere`, { detail: 'Edit the tab to limit it to certain cards' })
    if (inside.includes(id)) return void showToast(`“${t(id)}” is already in “${tabName}”`)
    withUndo(`Added “${t(id)}” to “${tabName}”`, (origin) => core.patchItem(ctx(origin), viewId, { props: { view: { ...view, filter: { ...view.filter, ancestors: [...inside, id] } } } }))
  },

  /**
   * A stack's charts, first to last: a tab's (kept on the tab, undoable), or the Done tab's
   * (household settings). `label`: say so in a toast with Undo (adding, removing, moving).
   */
  setCharts(home: ChartHome, charts: core.ChartSpec[], label?: string) {
    if (home.kind === 'done') {
      if (label) withUndo(label, (origin) => doc.transact(() => core.setHouseholdSetting(doc, 'doneCharts', charts), origin))
      else actions.setHousehold('doneCharts', charts)
      return
    }
    const view = store.get(home.id)?.props.view as SavedView | undefined
    if (!view) return
    const next: SavedView = { ...view, charts }
    if (!charts.length) delete next.charts
    if (label) withUndo(label, (origin) => core.patchItem(ctx(origin), home.id, { props: { view: next } }))
    else core.patchItem(ctx(), home.id, { props: { view: next } })
  },

  /** One chart's settings (applied as they're changed, in its ••• pop-up). */
  setChart(home: ChartHome, chart: core.ChartSpec) {
    actions.setCharts(
      home,
      chartsOf(home).map((c) => (c.id === chart.id ? chart : c)),
    )
  },

  /** Show or hide a stack's charts (the eye), for everyone. */
  setChartsHidden(home: ChartHome, hidden: boolean) {
    if (home.kind === 'done') return actions.setHousehold('doneChartsHidden', hidden)
    const view = store.get(home.id)?.props.view as SavedView | undefined
    if (!view || !!view.chartsHidden === hidden) return
    const next: SavedView = { ...view }
    if (hidden) next.chartsHidden = true
    else delete next.chartsHidden
    core.patchItem(ctx(), home.id, { props: { view: next } })
  },

  /** Drag a chart onto another tab: it moves there, and charts that tab from then on. */
  moveChart(from: ChartHome, chartId: string, to: ChartHome) {
    if (homeKey(from) === homeKey(to)) return
    const chart = chartsOf(from).find((c) => c.id === chartId)
    if (!chart) return
    const name = core.chartTitle(chart, store.fields())
    const where = to.kind === 'done' ? 'Done' : viewItemTitleOf(to.id)
    // Both sides in one change, so one Undo takes both back. The Done tab's charts are a
    // household setting, written with the action's origin like the tab's.
    const write = (origin: unknown) =>
      doc.transact(() => {
        const writes: Array<[ChartHome, core.ChartSpec[]]> = [
          [from, chartsOf(from).filter((c) => c.id !== chartId)],
          [to, [...chartsOf(to), chart]],
        ]
        for (const [home, charts] of writes) {
          if (home.kind === 'done') core.setHouseholdSetting(doc, 'doneCharts', charts)
          else {
            const view = store.get(home.id)?.props.view as SavedView | undefined
            if (view) core.patchItem(ctx(origin), home.id, { props: { view: { ...view, charts } } })
          }
        }
      }, origin)
    withUndo(`Moved “${name}” to “${where}”`, write)
  },

  /** A chart idea was seen (or turned down): it stays quiet there until the items change a lot. Not undoable. */
  markChartIdeas(home: ChartHome, keys: string[], count: number) {
    const seen = { ...ideasSeenOf(home) }
    if (keys.every((k) => seen[k] === count)) return
    for (const k of keys) seen[k] = count
    if (home.kind === 'done') return void actions.setHousehold('doneChartIdeas', seen)
    const view = store.get(home.id)?.props.view as SavedView | undefined
    if (view) core.patchItem(ctx(QUIET), home.id, { props: { view: { ...view, chartIdeas: seen } } })
  },

  /** Remove a view tab (the view item goes to Trash; the items it showed are untouched). */
  removeTab(viewId: string) {
    guard(() => withUndo('Tab removed', (origin) => core.trashItem(ctx(origin), viewId)))
  },

  createSavedView(name: string, view: SavedView): string {
    return core.addItem(ctx(), {
      parentId: null,
      title: name,
      done: null,
      props: { view },
      at: 'end',
    })
  },

  /** Bind edits to the Y.Text title directly (minimal diff). */
  setTitle(id: string, text: string) {
    const y = getYItem(doc, id)
    if (!y) return
    doc.transact(() => {
      core.applyTextDiff(core.ensureTitle(y), text)
      core.touch(y, myId())
    }, LOCAL)
  },

  exists(id: string) {
    return itemsMap(doc).get(id) instanceof Y.Map
  },
}

/** A row with nothing but a title: safe to merge/delete with Backspace. */
export function isPlainRow(it: core.ItemSnapshot): boolean {
  return (
    !it.bodyText &&
    !it.attachments.length &&
    !it.date &&
    !it.recur &&
    !it.remind?.length &&
    !it.assignee &&
    !it.tags.length &&
    !Object.keys(it.props).length &&
    !it.link &&
    !it.pinned &&
    !it.shareToken &&
    !it.completions.length &&
    !store.tree.childrenOf.get(it.id)?.length
  )
}

function attachmentKeys(atts: Attachment[]): string[] {
  return atts.flatMap((a) => (a.thumbKey ? [a.key, a.thumbKey] : [a.key]))
}

/** A tab's display name for messages (own name, or the cards it's inside). */
function viewItemTitleOf(viewId: string): string {
  const it = store.get(viewId)
  const inside = it?.props.view ? (core.normalizeView(it.props.view as SavedView).filter.ancestors ?? []) : []
  return displayTitle(it?.title ?? '', (x) => store.get(x)?.title) || inside.map((a) => t(a)).join(' + ') || 'View'
}
